import React, { SetStateAction, Dispatch } from "react";
import {
  Divider,
  FormControlLabel,
  FormControl,
  FormGroup,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import FormField from "components/FormField/FormField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CustomSelect, { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import {
  OrganizationProductType,
  ProductMeasurementMaterial,
  ProductType,
} from "generated/graphql";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import {
  jobMeasurementPlurals,
  default as getJobMeasurementMethodOptions,
  isSpecialUnitPriceProductType,
  specialUnitPriceMeasurementOptions,
} from "utils/product-types/getJobMeasurementMethodOptions";
import AreaCoverage from "./AreaCoverage";
import CreateAdditionalCostModal from "modules/additionalCosts/CreateAdditionalCostModal";
import MDButton from "components/MDButton";
import AdditionalCostOptions from "modules/additionalCosts/AdditionalCostOptions";
import EquipmentPieceOptions from "modules/equipmentPieces/EquipmentPieceOptions";
import CreateEquipmentPieceModal from "modules/equipmentPieces/CreateEquipmentPieceModal";
import { requiresPlant } from "DDD/action-objects/ProductCreate";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import currencyEndAdornment from "constants/currencyEndAdornment";
import WYSIWYG from "components/WYSIWYG/WYSIWYG";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import PlantProductsFields from "./PlantProductsFields";
import MaterialsNoPlantFields from "./MaterialsNoPlantFields";
import LabourFields from "./LabourFields";
import { useMemo } from "react";

export default function ProductFormFields({
  action,
  materialCostTypeOptions,
  labourCostTypeOptions,
  productType,
  isSubcontracted,
  setIsSubcontracted,
  isUnitPrice,
  setIsUnitPrice,
  canBeMeasuredInTons,
}: {
  action: "create" | "update";
  materialCostTypeOptions: SelectOptions;
  labourCostTypeOptions: SelectOptions;
  productType: OrganizationProductType;
  isUnitPrice: boolean;
  setIsUnitPrice: Dispatch<SetStateAction<boolean>>;
  isSubcontracted: boolean;
  setIsSubcontracted: Dispatch<SetStateAction<boolean>>;
  canBeMeasuredInTons: boolean;
}) {
  const {
    register,
    control,
    formState: { errors },
    getValues,
    watch,
    setValue,
  } = useFormContext();

  // const [isSubcontracted, setIsSubcontracted] = useState(false);

  const inputKey = action === "create" ? "connect" : "sync";

  const equipmentPieces = useFieldArray({
    control,
    name: `equipmentPieces.${inputKey}` as const,
    keyName: "key",
  });

  const additionalCosts = useFieldArray({
    control,
    name: `additionalCosts.${inputKey}` as const,
    keyName: "key",
  });

  const jobMeasurementMethod = watch("measurementJob");
  const measurementMaterial = watch("measurementMaterial");
  const productionRateType = watch("productionRateType");
  const plantProducts = watch(`plantProducts.${inputKey}`);
  const plantProductsCount = (plantProducts ?? [])?.length;
  const isSpecialUnitPriceType =
    isUnitPrice && isSpecialUnitPriceProductType(productType.type as ProductType);
  const measurementMethodOptions = useMemo(
    () => getJobMeasurementMethodOptions(productType.type as ProductType, { isUnitPrice }),
    [isUnitPrice, productType.type]
  );
  const selectedMeasurementMethod =
    isSpecialUnitPriceType && jobMeasurementMethod === "UNIT"
      ? measurementMaterial === ProductMeasurementMaterial.TON
        ? specialUnitPriceMeasurementOptions.PER_TON
        : specialUnitPriceMeasurementOptions.PER_UNIT
      : jobMeasurementMethod;

  const productionMeasurementLabel =
    isSpecialUnitPriceType && jobMeasurementMethod === "UNIT"
      ? jobMeasurementPlurals[measurementMaterial]
      : jobMeasurementPlurals[jobMeasurementMethod];

  const handleUnitPriceChange = (nextIsUnitPrice: boolean) => {
    const currentProductType = productType.type as ProductType;

    if (!isSpecialUnitPriceProductType(currentProductType)) {
      return;
    }

    if (nextIsUnitPrice) {
      if (
        jobMeasurementMethod !== "UNIT" &&
        measurementMaterial !== ProductMeasurementMaterial.TON
      ) {
        setValue("measurementMaterial", ProductMeasurementMaterial.TON, { shouldDirty: true });
      }

      if (
        jobMeasurementMethod === "UNIT" &&
        measurementMaterial !== ProductMeasurementMaterial.UNIT &&
        measurementMaterial !== ProductMeasurementMaterial.TON
      ) {
        setValue("measurementMaterial", ProductMeasurementMaterial.TON, { shouldDirty: true });
      }

      return;
    }

    if (jobMeasurementMethod === "UNIT") {
      const defaultMeasurementMethod = getJobMeasurementMethodOptions(currentProductType, {
        isUnitPrice: false,
      })[0]?.value;

      if (defaultMeasurementMethod) {
        setValue("measurementJob", defaultMeasurementMethod, { shouldDirty: true });
      }
    }

    if (measurementMaterial === ProductMeasurementMaterial.UNIT) {
      setValue("measurementMaterial", ProductMeasurementMaterial.TON, { shouldDirty: true });
    }
  };

  const handleMeasurementMethodChange = (nextValue: string) => {
    if (nextValue === specialUnitPriceMeasurementOptions.PER_UNIT) {
      setValue("measurementJob", "UNIT", { shouldDirty: true });
      setValue("measurementMaterial", ProductMeasurementMaterial.UNIT, {
        shouldDirty: true,
      });
      return;
    }

    if (nextValue === specialUnitPriceMeasurementOptions.PER_TON) {
      setValue("measurementJob", "UNIT", { shouldDirty: true });
      setValue("measurementMaterial", ProductMeasurementMaterial.TON, {
        shouldDirty: true,
      });
      return;
    }

    setValue("measurementJob", nextValue, { shouldDirty: true });
    setValue("measurementMaterial", ProductMeasurementMaterial.TON, {
      shouldDirty: true,
    });
  };

  return (
    <>
      <MDTypography variant="h5">
        Product Information <span style={{ color: "red" }}>*</span>
      </MDTypography>
      <MDBox mt={1}>
        <input type="hidden" {...register("organizationProductTypeId")} />
        <input type="hidden" {...register("productType")} />
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={12}>
            <MDBox display="flex" gap={2}>
              <Controller
                name="subcontracted"
                control={control}
                render={({ field }) => (
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          disabled={isUnitPrice}
                          onChange={(event) => {
                            field.onChange(event.target.checked);
                            setIsSubcontracted(event.target.checked);
                          }}
                        />
                      }
                      label="Subcontracted product"
                    />
                  </FormGroup>
                )}
              />
              {productType.type !== ProductType.GENERIC && (
                <Controller
                  name="unitPriceProduct"
                  control={control}
                  render={({ field }) => (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            disabled={isSubcontracted}
                            onChange={(event) => {
                              field.onChange(event.target.checked);
                              handleUnitPriceChange(event.target.checked);
                              setIsUnitPrice(event.target.checked);
                            }}
                          />
                        }
                        label="Unit Priced product"
                      />
                    </FormGroup>
                  )}
                />
              )}
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormField
              type="text"
              label="Name"
              placeholder="Name"
              error={errors.name}
              {...register("name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormField
              type="text"
              label="Proposal Product Name"
              placeholder="External Product Name"
              error={errors.externalProductName}
              {...register("externalProductName")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {isSpecialUnitPriceType ? (
              <Controller
                name="measurementJob"
                control={control}
                render={() => (
                  <FormControl fullWidth>
                    <InputLabel
                      shrink={!!selectedMeasurementMethod}
                      id="measurement-method-select-label"
                      sx={{
                        "&": {
                          marginLeft: "-12px",
                        },
                        ...(selectedMeasurementMethod
                          ? {
                              top: "8px",
                            }
                          : {}),
                      }}
                    >
                      Measurement Method
                    </InputLabel>
                    <Select
                      labelId="measurement-method-select-label"
                      value={selectedMeasurementMethod ?? ""}
                      variant="standard"
                      onChange={(event) => {
                        handleMeasurementMethodChange(event.target.value);
                      }}
                      sx={{
                        "& > #mui-component-select-role": {
                          padding: "0 0 5px !important",
                          minHeight: "unset",
                        },
                        ...(selectedMeasurementMethod
                          ? {
                              "& > div": {
                                marginTop: "6px",
                              },
                            }
                          : {}),
                      }}
                    >
                      {measurementMethodOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            ) : (
              <CustomSelect
                label="Measurement Method"
                control={control}
                fullWidth
                selectVariant="standard"
                data={measurementMethodOptions}
                name="measurementJob"
                {...register("measurementJob")}
              />
            )}
            <FormHelperText error sx={{ marginLeft: "0px" }}>
              {errors?.measurementJob?.message}
            </FormHelperText>
          </Grid>
          {isUnitPrice && (
            <>
              <Grid item xs={12} md={6}>
                <CurrencyFormField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  label="Unit Price"
                  {...currencyEndAdornment}
                  placeholder="Dollars"
                  name="unitPrice"
                  error={errors.unitPrice}
                  control={control}
                />
              </Grid>
              {canBeMeasuredInTons && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="productionRateType"
                    control={control}
                    render={({ field }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={(event) => {
                                field.onChange(event.target.checked);
                              }}
                            />
                          }
                          label="Use Tons Per Hour For Production Rate"
                        />
                      </FormGroup>
                    )}
                  />
                </Grid>
              )}
            </>
          )}
          {!isSubcontracted && !isUnitPrice && (
            <>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Material Cost Type"
                  control={control}
                  fullWidth
                  selectVariant="standard"
                  data={materialCostTypeOptions}
                  name="measurementMaterial"
                  {...register("measurementMaterial")}
                />
                <FormHelperText error sx={{ marginLeft: "0px" }}>
                  {errors?.measurementMaterial?.message}
                </FormHelperText>
              </Grid>
              {/* {!requiresPlant.includes(productType.type as ProductType) && (
                <Grid item xs={12} md={6}>
                  <CurrencyFormField
                    type="number"
                    inputProps={{ step: "0.01", min: "0" }}
                    label="Material Cost"
                    {...currencyEndAdornment}
                    placeholder="Dollars"
                    name="costMaterial"
                    error={errors.costMaterial}
                    control={control}
                  />
                </Grid>
              )} */}

              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Labor Cost Type"
                  control={control}
                  fullWidth
                  selectVariant="standard"
                  data={labourCostTypeOptions}
                  name="labourCostType"
                  {...register("labourCostType")}
                />
                <FormHelperText error sx={{ marginLeft: "0px" }}>
                  {errors?.labourCostType?.message}
                </FormHelperText>
              </Grid>
              <Grid item xs={12} md={6}>
                {canBeMeasuredInTons ? (
                  <FormField
                    type="number"
                    inputProps={{ step: "1" }}
                    disabled={!jobMeasurementMethod}
                    label={`${
                      !!productionRateType
                        ? "Crew production rate in tons per hour"
                        : `How many ${
                            jobMeasurementMethod
                              ? jobMeasurementPlurals[jobMeasurementMethod]
                              : "N/A"
                          } can your crew complete in a day?`
                    } `}
                    placeholder="1"
                    error={errors.dailyUnitsComplete}
                    {...register("dailyUnitsComplete", { valueAsNumber: true })}
                  />
                ) : (
                  <FormField
                    type="number"
                    inputProps={{ step: "1" }}
                    disabled={!jobMeasurementMethod}
                    label={`How many ${
                      jobMeasurementMethod ? jobMeasurementPlurals[jobMeasurementMethod] : "N/A"
                    } can your crew complete in a day?`}
                    placeholder="1"
                    error={errors.dailyUnitsComplete}
                    {...register("dailyUnitsComplete", { valueAsNumber: true })}
                  />
                )}
              </Grid>

              <AreaCoverage productType={productType} action={action} />
              {canBeMeasuredInTons && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="productionRateType"
                    control={control}
                    render={({ field }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={(event) => {
                                field.onChange(event.target.checked);
                              }}
                            />
                          }
                          label="Use Tons Per Hour For Production Rate"
                        />
                      </FormGroup>
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <CurrencyFormField
                  type="number"
                  label="Minimum Proposal Total Cost"
                  inputProps={{ max: "9999999" }}
                  placeholder="Dollars"
                  name="costMinimumProposal"
                  control={control}
                  {...currencyEndAdornment}
                  error={errors.costMinimumProposal}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CurrencyFormField
                  type="number"
                  label="Minimum Material Cost"
                  inputProps={{ max: "9999999" }}
                  placeholder="Dollars"
                  name="costMinimumProduct"
                  error={errors.costMinimumProduct}
                  {...currencyEndAdornment}
                  control={control}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="markupPercentage"
              render={({ field, fieldState: { error } }) => (
                <FormField
                  type="number"
                  inputProps={{ min: "0" }}
                  label="Markup Percentage"
                  placeholder="Percentage"
                  error={error}
                  {...field}
                  onChange={(event) => field.onChange(+event.target.value)}
                  value={field.value >= 0 ? `${field.value}` : ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormField
              type="number"
              inputProps={{ min: "0", max: "100" }}
              label="Indirect Percentage"
              placeholder="Percentage"
              name="indirectPercentage"
              error={errors.indirectPercentage}
              {...register("indirectPercentage", { valueAsNumber: true })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormField
              type="number"
              inputProps={{ min: "0", max: "100" }}
              label="Overhead Percentage"
              placeholder="Percentage"
              name="overheadPercentage"
              error={errors.overheadPercentage}
              {...register("overheadPercentage", { valueAsNumber: true })}
            />
          </Grid>
          {isUnitPrice && (
            <Grid item xs={12}>
              <Controller
                control={control}
                name="statementOfWork"
                render={({ field, fieldState: { error } }) => (
                  <>
                    <WYSIWYG
                      type="proposalProduct"
                      content={field.value}
                      onChange={(content) => field.onChange(content)}
                      title="Statement Of Work"
                      plantProductsCount={plantProductsCount}
                    />
                    {error && error?.message && (
                      <FormHelperText
                        sx={{
                          color: "red",
                        }}
                      >
                        {error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
          )}
          {isUnitPrice && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <MDTypography variant="h5">
                  Job Planning <span style={{ color: "#7B809A" }}>- optional</span>
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Material Cost Type"
                  control={control}
                  fullWidth
                  selectVariant="standard"
                  data={materialCostTypeOptions}
                  name="measurementMaterial"
                  disabled={isSpecialUnitPriceType && jobMeasurementMethod === "UNIT"}
                  {...register("measurementMaterial")}
                />
                <FormHelperText error sx={{ marginLeft: "0px" }}>
                  {errors?.measurementMaterial?.message}
                </FormHelperText>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Labor Cost Type"
                  control={control}
                  fullWidth
                  selectVariant="standard"
                  data={labourCostTypeOptions}
                  name="labourCostType"
                  {...register("labourCostType")}
                />
                <FormHelperText error sx={{ marginLeft: "0px" }}>
                  {errors?.labourCostType?.message}
                </FormHelperText>
              </Grid>
              <AreaCoverage productType={productType} action={action} />
              <Grid item xs={12} md={6}>
                <FormField
                  type="number"
                  inputProps={{ step: "1" }}
                  disabled={
                    isSpecialUnitPriceType && jobMeasurementMethod === "UNIT"
                      ? !measurementMaterial
                      : !jobMeasurementMethod
                  }
                  label={`How many ${
                    productionMeasurementLabel ? productionMeasurementLabel : "N/A"
                  } can your crew complete in a day?`}
                  placeholder="1"
                  error={errors.dailyUnitsComplete}
                  {...register("dailyUnitsComplete", { valueAsNumber: true })}
                />
              </Grid>
            </>
          )}
          {!isUnitPrice && (
            <Grid item xs={12}>
              <Controller
                control={control}
                name="statementOfWork"
                render={({ field, fieldState: { error } }) => (
                  <>
                    <WYSIWYG
                      type="proposalProduct"
                      content={field.value}
                      onChange={(content) => field.onChange(content)}
                      title="Statement Of Work"
                      plantProductsCount={plantProductsCount}
                    />
                    {error && error?.message && (
                      <FormHelperText
                        sx={{
                          color: "red",
                        }}
                      >
                        {error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
          )}
          {!isSubcontracted && <LabourFields action={action} />}
          {!isSubcontracted && requiresPlant.includes(productType.type as ProductType) && (
            <PlantProductsFields action={action} productType={productType} />
          )}
          {!isSubcontracted && !requiresPlant.includes(productType.type as ProductType) && (
            <Grid item xs={12}>
              <MaterialsNoPlantFields action={action} />
            </Grid>
          )}
          {!isSubcontracted && !isUnitPrice && (
            <>
              <Grid item xs={12}>
                <EquipmentPieceOptions>
                  {({ equipmentPieceOptions, loading, search, pagination }) => (
                    <>
                      <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDTypography variant="h6">Equipment Pieces</MDTypography>
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <MDButton
                            size="small"
                            type="button"
                            variant="contained"
                            color="blue"
                            onClick={() => {
                              equipmentPieces.append({ id: null, quantity: 1, name: "" });
                            }}
                          >
                            Add Equipment Piece
                          </MDButton>
                          <CreateEquipmentPieceModal size="small" variant="contained" />
                        </MDBox>
                      </MDBox>
                      {equipmentPieceOptions.length > 0 || equipmentPieces.fields.length > 0
                        ? equipmentPieces.fields.map((field, index) => (
                            <Grid container key={field.key} spacing={1} mb={2}>
                              <Grid item xs={12} md={6}>
                                <Controller
                                  name={`equipmentPieces.${inputKey}.${index}.id` as const}
                                  control={control}
                                  render={({ field }) => (
                                    <ScrollAsyncAutocomplete
                                      {...field}
                                      value={{
                                        label:
                                          equipmentPieceOptions.find((x) => x.value === field.value)
                                            ?.label ??
                                          getValues(`equipmentPieces.${inputKey}.${index}.name`) ??
                                          "",
                                        value: field.value,
                                      }}
                                      options={equipmentPieceOptions}
                                      loading={loading}
                                      label="Equipment Piece"
                                      search={search}
                                      onLoadMore={pagination.loadMore}
                                      hasMore={pagination.hasNextPage}
                                      error={String(
                                        errors.equipmentPieces?.[inputKey]?.[index]?.id?.message ??
                                          ""
                                      )}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={5.5}>
                                <FormField
                                  type="number"
                                  // Allow decimal quantities and explicit 0 when entering
                                  // equipment piece quantity overrides.
                                  inputProps={{ step: "0.01", min: "0" }}
                                  placeholder="Equipment Piece Quantity"
                                  label="Equipment Piece Quantity"
                                  error={errors["equipmentPieces"]?.[inputKey]?.[index]?.quantity}
                                  {...register(`equipmentPieces.${inputKey}.${index}.quantity`, {
                                    valueAsNumber: true,
                                  })}
                                />
                              </Grid>
                              <Grid item xs={12} md={0.5}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                  height="100%"
                                >
                                  <IconButton
                                    //@ts-ignore
                                    color="negative"
                                    onClick={() => {
                                      equipmentPieces.remove(index);
                                    }}
                                  >
                                    <Icon fontSize="large" color="inherit">
                                      delete
                                    </Icon>
                                  </IconButton>
                                </MDBox>
                              </Grid>
                            </Grid>
                          ))
                        : null}
                    </>
                  )}
                </EquipmentPieceOptions>
              </Grid>
            </>
          )}
          {!isSubcontracted && (
            <Grid item xs={12} mb={3}>
              <AdditionalCostOptions>
                {({ additionalCostOptions, loading, search, pagination }) => (
                  <>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                      <MDTypography variant="h6">Additional Costs</MDTypography>
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <MDButton
                          size="small"
                          type="button"
                          variant="contained"
                          color="blue"
                          onClick={() => {
                            additionalCosts.append({ id: null, type: null, name: "" });
                          }}
                        >
                          Add Additional Costs
                        </MDButton>
                        <CreateAdditionalCostModal size="small" variant="contained" />
                      </MDBox>
                    </MDBox>
                    {errors.additionalCosts?.[inputKey]?.message && (
                      <FormHelperText error sx={{ marginLeft: 0, marginTop: 1 }}>
                        {String(errors.additionalCosts[inputKey]?.message)}
                      </FormHelperText>
                    )}

                    {additionalCostOptions.length > 0 || additionalCosts.fields.length > 0
                      ? additionalCosts.fields.map((field, index) => (
                          <Grid container key={field.key} spacing={1} mb={2} mt={1}>
                            <Grid item xs={12} md={11.5}>
                              <Controller
                                name={`additionalCosts.${inputKey}.${index}.id`}
                                control={control}
                                render={({ field }) => (
                                  <ScrollAsyncAutocomplete
                                    {...field}
                                    value={{
                                      label:
                                        additionalCostOptions.find((x) => x.value === field.value)
                                          ?.label ??
                                        getValues(`additionalCosts.${inputKey}.${index}.name`) ??
                                        "",
                                      value: field.value,
                                    }}
                                    options={additionalCostOptions}
                                    loading={loading}
                                    label="Additional cost"
                                    search={search}
                                    onLoadMore={pagination.loadMore}
                                    hasMore={pagination.hasNextPage}
                                    error={String(
                                      errors.additionalCosts?.[inputKey]?.[index]?.id?.message ?? ""
                                    )}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12} md={0.5}>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                height="100%"
                              >
                                <IconButton
                                  //@ts-ignore
                                  color="negative"
                                  onClick={() => {
                                    additionalCosts.remove(index);
                                  }}
                                >
                                  <Icon fontSize="large" color="inherit">
                                    delete
                                  </Icon>
                                </IconButton>
                              </MDBox>
                            </Grid>
                          </Grid>
                        ))
                      : null}
                  </>
                )}
              </AdditionalCostOptions>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </>
  );
}
